import { EmailDesign, EmailBlock, TextBlockContent, ImageBlockContent, ButtonBlockContent, DividerBlockContent, SpacerBlockContent, SocialBlockContent, HtmlBlockContent, LayoutBlockContent, HeaderBlockContent, FooterBlockContent } from '../types';

/**
 * Convert EmailDesign to email-safe HTML with inline styles
 */
export function generateHtml(design: EmailDesign): string {
  const containerStyle = {
    backgroundColor: design.styles?.backgroundColor || '#ffffff',
    width: design.styles?.width || '600px',
    maxWidth: design.styles?.maxWidth || '600px',
    margin: '0 auto',
    fontFamily: design.styles?.fontFamily || 'Arial, sans-serif',
    fontSize: `${design.styles?.fontSize || 14}px`,
    color: design.styles?.textColor || '#000000'
  };

  const blocksHtml = design.blocks.map(block => renderBlock(block)).join('\n');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Template</title>
  <style>
    /* Email client compatibility */
    body {
      margin: 0;
      padding: 0;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    table {
      border-collapse: collapse;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    img {
      border: 0;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
      -ms-interpolation-mode: bicubic;
    }
    @media only screen and (max-width: 600px) {
      .email-container {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f3f4f6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="${containerStyle.width}" class="email-container" style="${objectToInlineStyles(containerStyle)}">
          ${blocksHtml}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Render a single block to HTML
 */
function renderBlock(block: EmailBlock): string {
  switch (block.type) {
    case 'text':
      return renderTextBlock(block);
    case 'image':
      return renderImageBlock(block);
    case 'button':
      return renderButtonBlock(block);
    case 'divider':
      return renderDividerBlock(block);
    case 'spacer':
      return renderSpacerBlock(block);
    case 'social':
      return renderSocialBlock(block);
    case 'html':
      return renderHtmlBlock(block);
    case 'row':
      return renderRowBlock(block);
    case 'column':
      return renderColumnBlock(block);
    case 'header':
      return renderHeaderBlock(block);
    case 'footer':
      return renderFooterBlock(block);
    default:
      return '';
  }
}

function renderTextBlock(block: EmailBlock): string {
  const content = block.content as TextBlockContent;
  const style = {
    fontSize: `${content.fontSize || 16}px`,
    fontFamily: content.fontFamily || 'Arial, sans-serif',
    fontWeight: content.fontWeight || 'normal',
    fontStyle: content.fontStyle || 'normal',
    color: content.color || '#000000',
    backgroundColor: content.backgroundColor || 'transparent',
    textAlign: content.textAlign || 'left',
    lineHeight: content.lineHeight || 1.5,
    padding: content.padding || '10px',
    margin: content.margin || '0',
    ...block.styles
  };

  const text = (content.text || '').replace(/\n/g, '<br/>');
  
  if (content.link) {
    return `
      <tr>
        <td style="${objectToInlineStyles(style)}">
          <a href="${content.link}" style="color: ${content.color || '#000000'}; text-decoration: none;">${text}</a>
        </td>
      </tr>
    `;
  }

  return `
    <tr>
      <td style="${objectToInlineStyles(style)}">${text}</td>
    </tr>
  `;
}

function renderImageBlock(block: EmailBlock): string {
  const content = block.content as ImageBlockContent;
  if (!content.src) return '';

  const containerStyle = {
    padding: '10px',
    textAlign: content.align || 'center',
    ...block.styles
  };

  const imgStyle = {
    width: content.width || '100%',
    height: content.height || 'auto',
    borderRadius: content.borderRadius || '0',
    maxWidth: '100%',
    display: 'block',
    margin: '0 auto'
  };

  const imgTag = `<img src="${content.src}" alt="${content.alt || ''}" style="${objectToInlineStyles(imgStyle)}" />`;
  
  if (content.link) {
    return `
      <tr>
        <td style="${objectToInlineStyles(containerStyle)}">
          <a href="${content.link}">${imgTag}</a>
        </td>
      </tr>
    `;
  }

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">${imgTag}</td>
    </tr>
  `;
}

function renderButtonBlock(block: EmailBlock): string {
  const content = block.content as ButtonBlockContent;
  const containerStyle = {
    padding: '10px',
    textAlign: content.align || 'center',
    ...block.styles
  };

  const buttonStyle = {
    backgroundColor: content.backgroundColor || '#3b82f6',
    color: content.textColor || '#ffffff',
    fontSize: `${content.fontSize || 16}px`,
    fontWeight: content.fontWeight || 'bold',
    padding: content.padding || '12px 24px',
    borderRadius: content.borderRadius || '4px',
    borderWidth: content.borderWidth || '0',
    borderColor: content.borderColor || 'transparent',
    borderStyle: content.borderStyle || 'solid',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center',
    width: content.width || 'auto'
  };

  const text = content.text || 'Click here';
  const link = content.link || '#';

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">
        <a href="${link}" style="${objectToInlineStyles(buttonStyle)}">${text}</a>
      </td>
    </tr>
  `;
}

function renderDividerBlock(block: EmailBlock): string {
  const content = block.content as DividerBlockContent;
  const containerStyle = {
    padding: content.spacing || '20px 0',
    textAlign: content.align || 'center',
    ...block.styles
  };

  const dividerStyle = {
    borderTop: `${content.thickness || 1}px ${content.style || 'solid'} ${content.color || '#e5e7eb'}`,
    width: content.width || '100%',
    margin: '0 auto',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none'
  };

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">
        <div style="${objectToInlineStyles(dividerStyle)}"></div>
      </td>
    </tr>
  `;
}

function renderSpacerBlock(block: EmailBlock): string {
  const content = block.content as SpacerBlockContent;
  return `
    <tr>
      <td style="height: ${content.height || 20}px; line-height: ${content.height || 20}px;">&nbsp;</td>
    </tr>
  `;
}

function renderSocialBlock(block: EmailBlock): string {
  const content = block.content as SocialBlockContent;
  if (!content.platforms || content.platforms.length === 0) return '';

  const containerStyle = {
    padding: '20px',
    textAlign: content.align || 'center',
    ...block.styles
  };

  // Note: For email HTML, we'd need to use image-based social icons
  // This is a simplified version - in production, use actual icon images
  const iconsHtml = content.platforms.map((platform, index) => {
    const spacing = index > 0 ? `margin-left: ${content.spacing || 10}px;` : '';
    return `<a href="${platform.url}" style="display: inline-block; ${spacing}">[${platform.platform}]</a>`;
  }).join('');

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">
        ${iconsHtml}
      </td>
    </tr>
  `;
}

function renderHtmlBlock(block: EmailBlock): string {
  const content = block.content as HtmlBlockContent;
  const containerStyle = {
    padding: '10px',
    ...block.styles
  };

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">
        ${content.html || ''}
      </td>
    </tr>
  `;
}

function renderRowBlock(block: EmailBlock): string {
  const content = block.content as LayoutBlockContent;
  const containerStyle = {
    backgroundColor: content.backgroundColor || 'transparent',
    padding: content.padding || '0',
    ...block.styles
  };

  const columns = block.children || [];
  const columnWidth = columns.length > 0 ? `${100 / columns.length}%` : '100%';

  const columnsHtml = columns.map(child => {
    const childHtml = renderBlock(child);
    return `
      <td style="width: ${columnWidth}; vertical-align: top;">
        ${childHtml}
      </td>
    `;
  }).join('');

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)}">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            ${columnsHtml}
          </tr>
        </table>
      </td>
    </tr>
  `;
}

function renderColumnBlock(block: EmailBlock): string {
  const content = block.content as LayoutBlockContent;
  const containerStyle = {
    backgroundColor: content.backgroundColor || 'transparent',
    padding: content.padding || '10px',
    ...block.styles
  };

  const childrenHtml = block.children 
    ? block.children.map(child => renderBlock(child)).join('')
    : '';

  return `
    <td style="${objectToInlineStyles(containerStyle)}">
      ${childrenHtml}
    </td>
  `;
}

function renderHeaderBlock(block: EmailBlock): string {
  const content = block.content as HeaderBlockContent;
  const containerStyle = {
    backgroundColor: content.backgroundColor || '#ffffff',
    padding: content.padding || '20px',
    ...block.styles
  };

  const logoHtml = content.logo?.src 
    ? content.logo.link
      ? `<a href="${content.logo.link}"><img src="${content.logo.src}" alt="${content.logo.alt || 'Logo'}" style="width: ${content.logo.width || '150px'}; max-width: 100%; height: auto;" /></a>`
      : `<img src="${content.logo.src}" alt="${content.logo.alt || 'Logo'}" style="width: ${content.logo.width || '150px'}; max-width: 100%; height: auto;" />`
    : '';

  const navHtml = content.navigation && content.navigation.length > 0
    ? `<nav style="display: flex; gap: 16px; flex-wrap: wrap;">
        ${content.navigation.map(item => 
          `<a href="${item.link}" style="color: #374151; text-decoration: none; font-size: 14px;">${item.text}</a>`
        ).join('')}
      </nav>`
    : '';

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)} border-bottom: 1px solid #e5e7eb;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr>
            <td style="vertical-align: middle;">
              ${logoHtml}
            </td>
            ${navHtml ? `<td style="vertical-align: middle; text-align: right;">${navHtml}</td>` : ''}
          </tr>
        </table>
      </td>
    </tr>
  `;
}

function renderFooterBlock(block: EmailBlock): string {
  const content = block.content as FooterBlockContent;
  const containerStyle = {
    backgroundColor: content.backgroundColor || '#f3f4f6',
    padding: content.padding || '30px 20px',
    color: content.textColor || '#6b7280',
    fontSize: `${content.fontSize || 12}px`,
    ...block.styles
  };

  const companyInfoHtml = content.companyInfo 
    ? `<div style="margin-bottom: 16px;">${content.companyInfo}</div>`
    : '';

  const socialHtml = content.socialLinks && content.socialLinks.length > 0
    ? `<div style="margin-bottom: 16px;">
        ${content.socialLinks.map(social => {
          // Note: In actual email HTML, you'd use image-based icons
          // For now, using text links
          return `<a href="${social.url}" style="color: ${content.textColor || '#6b7280'}; text-decoration: none; margin-right: 12px; font-size: ${content.fontSize || 12}px;">[${social.platform}]</a>`;
        }).join('')}
      </div>`
    : '';

  const unsubscribeHtml = content.showUnsubscribeLink !== false
    ? `<a href="{{unsubscribeLink}}" style="color: ${content.textColor || '#6b7280'}; text-decoration: underline; font-size: ${content.fontSize || 12}px; margin-right: 16px;">${content.unsubscribeText || 'Unsubscribe'}</a>`
    : '';

  const preferenceCenterHtml = content.showPreferenceCenter !== false
    ? `<a href="{{preferenceCenterLink}}" style="color: ${content.textColor || '#6b7280'}; text-decoration: underline; font-size: ${content.fontSize || 12}px;">${content.preferenceCenterText || 'Manage Preferences'}</a>`
    : '';

  const subscriptionHtml = (unsubscribeHtml || preferenceCenterHtml)
    ? `<div style="margin-bottom: 16px; font-size: ${content.fontSize || 12}px;">
        ${unsubscribeHtml}
        ${preferenceCenterHtml}
      </div>`
    : '';

  const copyrightHtml = content.copyrightText
    ? `<div style="font-size: ${content.fontSize || 12}px; opacity: 0.75; margin-top: 16px;">
        ${content.copyrightText}
      </div>`
    : '';

  return `
    <tr>
      <td style="${objectToInlineStyles(containerStyle)} border-top: 1px solid #e5e7eb;">
        ${companyInfoHtml}
        ${socialHtml}
        ${subscriptionHtml}
        ${copyrightHtml}
      </td>
    </tr>
  `;
}

/**
 * Convert a style object to inline CSS string
 */
function objectToInlineStyles(styles: Record<string, any>): string {
  return Object.entries(styles)
    .map(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value};`;
    })
    .join(' ');
}

/**
 * Generate plain text version from HTML
 */
export function generatePlainText(html: string): string {
  // Remove HTML tags and decode entities
  const text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  return text;
}
